// Custom store. The main get route fetches data from a custom route.

import {
  IBuildingsHasPhotos, ISuiteTypesFloorplans, ISuitesHasPhotos, ISuiteTypes,
} from '@llws/lift-entity-interfaces';
import { ActionContext } from 'vuex';
import { cloneObject, IApiResponse, IModifyPayload, subsetObject } from '@/shared';
import { adminRequest } from '@/shared/request-configs/admin-request';

export interface IUnitMediaStoreState {
  suitesHasPhotos: ISuitesHasPhotos[];
  unitFloorplans: ISuiteTypesFloorplans[];
  videoId: null | number;
  virtualTourId: null | number;
}

export interface IUnitMediaStoreStateWithStateCopy extends IUnitMediaStoreState {
  initialMedia?: IUnitMediaStoreState | null;
}

const initialState: IUnitMediaStoreStateWithStateCopy = {
  suitesHasPhotos: [],
  unitFloorplans: [],
  videoId: null,
  virtualTourId: null,
  initialMedia: null,
};

export default {
  namespaced: true, // 'unit-media',

  state: initialState,

  getters: {
    media: (state: IUnitMediaStoreStateWithStateCopy) => subsetObject(state, ['stateCopy']) as IUnitMediaStoreState,

    initialMedia: (state: IUnitMediaStoreStateWithStateCopy) => state.initialMedia,

    unitMedia: (state: IUnitMediaStoreStateWithStateCopy) => subsetObject(state, ['stateCopy']) as IUnitMediaStoreState,

    initialUnitMedia: (state: IUnitMediaStoreStateWithStateCopy) => state.initialMedia,
  },

  mutations: {
    set: (state: IUnitMediaStoreStateWithStateCopy, payload: IUnitMediaStoreState) => {
      state.suitesHasPhotos = payload.suitesHasPhotos;
      state.unitFloorplans = payload.unitFloorplans;
      state.videoId = payload.videoId;
      state.virtualTourId = payload.virtualTourId;
    },

    setByKey: (
      state: IUnitMediaStoreStateWithStateCopy,
      payload: {
        key: 'suitesHasPhotos' | 'unitFloorplans' | 'videoId' | 'virtualTourId',
        data: IBuildingsHasPhotos[] & ISuiteTypesFloorplans[] & ISuitesHasPhotos[] & number & IUnitMediaStoreState,
        setInitialMedia: boolean
      },
    ) => {
      state[payload.key] = payload.data;
      if (state && payload.setInitialMedia && state.initialMedia) {
        state.initialMedia[payload.key] = payload.data;
      }
    },

  },

  actions: {
    get: async (ctx: ActionContext<IUnitMediaStoreState, any>, unit: ISuiteTypes) => {
      const mediaState = {
        suitesHasPhotos: cloneObject(unit.suitesHasPhotos || [])?.sort((a, b) => a.orderBy - b.orderBy),
        unitFloorplans: cloneObject(unit.suiteTypesFloorplans || [])?.sort((a, b) => a.orderBy - b.orderBy),
        videoId: unit.videoId ?? null, // make sure it is not undefined
        virtualTourId: unit.virtualTourId ?? null, // make sure it is not undefined
      } as IUnitMediaStoreState;

      ctx.commit('set', mediaState);
      ctx.commit('setByKey', { key: 'initialMedia', data: mediaState });
    },

    update: async (ctx: ActionContext<IUnitMediaStoreState, any>, payload: IModifyPayload<number, Partial<ISuiteTypes>>) => {
      const unit: IApiResponse<ISuiteTypes, null> = await adminRequest.get(`/property-api/units/${payload.parentId}`);

      if (unit.data) {
        const updatedData: IApiResponse<ISuiteTypes, null> = await adminRequest.put(`/property-api/units/${payload.parentId}`, {
          ...payload.data,
          id: payload.parentId,
          clientId: unit.data.clientId,
          buildingId: unit.data.buildingId,
          modifiedUserId: unit.data.modifiedUserId,
        });

        if (unit.data) {
          ctx.commit('set', { ...ctx.state, virtualTourId: updatedData.data?.virtualTourId, videoId: updatedData.data?.videoId });
          ctx.commit('setByKey', { key: 'initialMedia', data: { ...ctx.state, virtualTourId: updatedData.data?.virtualTourId, videoId: updatedData.data?.videoId } });
        }
      }
    },
  },

};
